import { c as _c } from "react/compiler-runtime";
import { IcoCheck16, IcoClock16, IcoClose16, IcoMinusSmall16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import { AddSectionButton, AddSectionOverlay, type ArtifactTone, toneStyles } from '@/components/block-primitives';
import type { ChecklistBlockConfig, ChecklistItem, ChecklistState } from './artifact-block-types';
import EditableText from './editable-text';

// Each state maps to a tone (for the icon chip color) and an icon; srLabel names the
// state for assistive tech, since the icon alone carries that meaning visually.
const stateConfig: Record<ChecklistState, {
  tone: ArtifactTone;
  icon: typeof IcoCheck16;
  srLabel: string;
}> = {
  complete: {
    tone: 'positive',
    icon: IcoCheck16,
    srLabel: 'Complete'
  },
  pending: {
    tone: 'warning',
    icon: IcoClock16,
    srLabel: 'Pending'
  },
  blocked: {
    tone: 'critical',
    icon: IcoClose16,
    srLabel: 'Blocked'
  },
  na: {
    tone: 'neutral',
    icon: IcoMinusSmall16,
    srLabel: 'Not applicable'
  }
};
type ArtifactChecklistBlockProps = {
  config: ChecklistBlockConfig;
  editable?: boolean;
  onChange?: (next: ChecklistBlockConfig) => void;
};
const ArtifactChecklistBlock = t0 => {
  const $ = _c(44);
  const {
    config,
    editable: t1,
    onChange
  } = t0;
  const editable = t1 === undefined ? false : t1;
  const [addTitle, setAddTitle] = useState(false);
  const [addNote, setAddNote] = useState(false);
  let t2;
  if ($[0] !== config || $[1] !== onChange) {
    t2 = next => onChange?.({
      ...config,
      ...next
    });
    $[0] = config;
    $[1] = onChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const patch = t2;
  let t3;
  if ($[3] !== config.items || $[4] !== patch) {
    t3 = (index, next_0) => patch({
      items: config.items.map((item, i) => i === index ? {
        ...item,
        ...next_0
      } : item)
    });
    $[3] = config.items;
    $[4] = patch;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const patchItem = t3;
  const showTitle = !!config.title || addTitle;
  const showNote = !!config.note || addNote;
  let t4;
  if ($[6] !== editable || $[7] !== showTitle) {
    t4 = editable && !showTitle && <AddSectionOverlay position="top"><AddSectionButton label="Add a title" onClick={() => setAddTitle(true)} /></AddSectionOverlay>;
    $[6] = editable;
    $[7] = showTitle;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== editable || $[10] !== showNote) {
    t5 = editable && !showNote && <AddSectionOverlay position="bottom"><AddSectionButton label="Add a note" onClick={() => setAddNote(true)} /></AddSectionOverlay>;
    $[9] = editable;
    $[10] = showNote;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = config.title || "Checklist";
  let t7;
  if ($[12] !== addTitle || $[13] !== config.title || $[14] !== editable || $[15] !== patch || $[16] !== showTitle) {
    t7 = showTitle && <EditableText autoFocus={addTitle} block={true} className="text-label-2 text-primary" editable={editable} label="Checklist title" onSubmit={title => patch({
      title
    })} onEmptyBlur={() => setAddTitle(false)} optional={true} placeholder="Add a title" value={config.title ?? ""} />;
    $[12] = addTitle;
    $[13] = config.title;
    $[14] = editable;
    $[15] = patch;
    $[16] = showTitle;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== config.items || $[19] !== editable || $[20] !== patchItem) {
    let t9;
    if ($[22] !== editable || $[23] !== patchItem) {
      t9 = (item_0, index_0) => {
        const {
          tone,
          icon: Icon,
          srLabel
        } = stateConfig[item_0.state];
        return <li className="flex items-start gap-3" key={index_0}><span className={cx("flex h-5 w-5 shrink-0 items-center justify-center rounded-md", toneStyles[tone].surface)}><Icon color={toneStyles[tone].icon} aria-label={srLabel} /></span><div className="flex min-w-0 flex-1 flex-col gap-2"><EditableText as="div" className={cx("text-label-2", item_0.state === "na" ? "text-tertiary" : "text-primary")} editable={editable} label="Item label" onSubmit={label => patchItem(index_0, {
              label
            })} value={item_0.label} />{(editable || item_0.detail) && <EditableText as="div" className="break-words text-body-2 text-tertiary" editable={editable} label="Item detail" onSubmit={detail => patchItem(index_0, {
              detail
            })} optional={true} placeholder="Add detail" value={item_0.detail ?? ""} />}</div></li>;
      };
      $[22] = editable;
      $[23] = patchItem;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    t8 = config.items.map(t9);
    $[18] = config.items;
    $[19] = editable;
    $[20] = patchItem;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[25] !== t8) {
    t9 = <ul className="flex flex-col gap-5">{t8}</ul>;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== t7 || $[28] !== t9) {
    t10 = <div className="flex flex-col gap-4 px-4 py-4 md:px-6 md:py-5">{t7}{t9}</div>;
    $[27] = t7;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== addNote || $[31] !== config.note || $[32] !== editable || $[33] !== patch || $[34] !== showNote) {
    t11 = showNote && <div className="border-t border-tertiary bg-secondary px-4 md:px-6 py-3 text-caption-4 text-tertiary"><EditableText autoFocus={addNote} block={true} editable={editable} label="Checklist note" onSubmit={note => patch({
        note
      })} onEmptyBlur={() => setAddNote(false)} optional={true} placeholder="Add a note" value={config.note ?? ""} /></div>;
    $[30] = addNote;
    $[31] = config.note;
    $[32] = editable;
    $[33] = patch;
    $[34] = showNote;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t6) {
    t12 = <section aria-label={t6} className="overflow-hidden rounded-lg border border-tertiary bg-primary">{t10}{t11}</section>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t6;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t12 || $[41] !== t4 || $[42] !== t5) {
    t13 = <div className="group relative">{t4}{t5}{t12}</div>;
    $[40] = t12;
    $[41] = t4;
    $[42] = t5;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  return t13;
};
export default ArtifactChecklistBlock;